import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Ambulance, Star, Loader, AlertTriangle, CreditCard, Wallet, Building, Smartphone } from 'lucide-react';
import { Header,
    Body,
    VehicleCard,
    RecommendedVehicleCard,
    ButtonGroup,
    Button,
    FormGroup,
    Label } from '../StyledComponents';
import { animations } from '../animations';
import { ProfessionalLoader } from '../components/ProfessionalLoader';

export const VehicleStep = ({
  recommendedVehicle,
  vehicleTypes,
  selectedVehicleType,
  selectedVehicleTypeUUId,
  onVehicleSelect,
  onPrevious,
  onSubmit,
  noResults,
  onRetry,
  isLoading,
  retryCount
}) => {
  const [paymentMethod, setPaymentMethod] = useState('Cash');

  // Payment method options with icons
  const paymentOptions = [
    { value: 'Cash', label: 'Cash Payment', icon: Wallet, description: 'Pay with cash on delivery' },
    { value: 'Card', label: 'Card Payment', icon: CreditCard, description: 'Pay with credit/debit card' },
    { value: 'Tele-birr', label: 'Tele-birr', icon: Smartphone, description: 'Pay with Tele-birr mobile wallet' },
    { value: 'Corporate', label: 'Corporate Account', icon: Building, description: 'Use company account balance' }
  ];

  // Handle submit with payment method
  const handleSubmitWithPayment = () => {
    // Pass payment method to parent component
    onSubmit(paymentMethod);
  };

  // Debug mounting/unmounting
  useEffect(() => {
    console.log("VehicleStep mounted", { recommendedVehicle, vehicleTypes });
    return () => {
      console.log("VehicleStep unmounted");
    };
  }, [recommendedVehicle, vehicleTypes]);

  // Check if data is available (alternatives or regular vehicles)
  const hasVehicles = Array.isArray(vehicleTypes) && vehicleTypes.length > 0;
  const hasAvailableVehicles = hasVehicles && vehicleTypes.some(v => v.available !== false);
  
  // Show no results message if noResults is present
  if (noResults) {
    return (
      <motion.div {...animations.pageTransition}>
        <Header>
          <Ambulance />
          <h2>No Vehicles Found</h2>
        </Header>
        <Body>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '2rem' }}>
            <AlertTriangle size={32} style={{ color: '#dc2626', marginBottom: 16 }} />
            <p style={{ textAlign: 'center', marginBottom: 16 }}>
              {noResults.message || 'No vehicles are currently available for your emergency.'}
            </p>
            {noResults.distance && (
              <p style={{ color: '#64748b', marginBottom: 16 }}>
                Closest responder distance: {noResults.distance}
              </p>
            )}
            <Button variant="primary" onClick={onRetry} whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.98 }}>
              Retry Search {retryCount > 0 ? `(Attempt ${retryCount + 1})` : ''}
            </Button>
          </div>
          <ButtonGroup>
            <Button variant="secondary" onClick={onPrevious}>
              Previous
            </Button>
          </ButtonGroup>
        </Body>
      </motion.div>
    );
  }

  return (
    <motion.div {...animations.pageTransition} style={{ position: 'relative', minHeight: '80vh' }}>
      {/* Loading overlay - ProfessionalLoader handles its own positioning */}
      {isLoading ? (
        <ProfessionalLoader
          isVisible={isLoading}
          title="Searching for Ambulances"
          subtitle="Please wait while we find the best vehicles for your emergency..."
          showSteps={true}
          currentStep={2}
        />
      ) : (
        <>
          <Header>
            <Ambulance />
            <h2>Select Vehicle</h2>
          </Header>
          <Body>
        {recommendedVehicle && (
          <RecommendedVehicleCard
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.3 }}
          >
            <div className="header">
              <Star size={20} />
              <h3>Recommended Vehicle</h3>
            </div>
            <p style={{ color: '#92400e', marginBottom: '1rem', fontSize: '0.9rem' }}>
              Based on your severity assessment and location, we recommend:
            </p>
            <label>
              <input
                type="radio"
                name="vehicleType"
                checked={selectedVehicleTypeUUId === recommendedVehicle.uuid}
                onChange={() => onVehicleSelect(recommendedVehicle.uuid, recommendedVehicle.id)}
              />
              <div className="details">
                <h4>{recommendedVehicle.typeName}</h4>
                <p>Estimated Price: {recommendedVehicle?.estimatedPrice} Birr</p>
              </div>
            </label>
          </RecommendedVehicleCard>
        )}

        <div className="vehicle-list">
          {vehicleTypes && vehicleTypes
            // Filter only available alternatives
            .filter(vehicle => vehicle.available !== false)
            .map(vehicle => (
              <VehicleCard key={vehicle.id}>
                <label>
                  <input
                    type="radio"
                    name="vehicleType"
                    checked={selectedVehicleTypeUUId === vehicle.uuid}
                    onChange={() => onVehicleSelect(vehicle.uuid, vehicle.id)}
                  />
                  <div className="details">
                    <h4>{vehicle.typeName || vehicle.type}</h4>
                    <p>Status: {vehicle.status || 'Available'}</p>
                    {vehicle.estimatedPrice && (
                      <p>Estimated Price: {vehicle.estimatedPrice} Birr</p>
                    )}
                    {vehicle.estimatedArrival && (
                      <p>Estimated Arrival: {typeof vehicle.estimatedArrival === 'string' ? vehicle.estimatedArrival : 
                        vehicle.estimatedArrival.firstResponder || vehicle.estimatedArrival.ambulance || 'N/A'}</p>
                    )}
                  </div>
                </label>
              </VehicleCard>
            ))}
        </div>

        {/* Show message if no additional vehicles besides recommended */}
        {vehicleTypes && vehicleTypes.filter(v => v.available !== false).length === 0 && recommendedVehicle && (
          <p style={{ textAlign: 'center', margin: '1rem 0' }}>
            No additional vehicles available at this time.
          </p>
        )}
        
        {/* Show unavailable alternatives as disabled options */}
        {vehicleTypes && vehicleTypes.filter(v => v.available === false).length > 0 && (
          <div style={{ marginTop: '1rem', opacity: 0.6 }}>
            <h4 style={{ color: '#64748b', marginBottom: '0.5rem' }}>Currently Unavailable:</h4>
            {vehicleTypes.filter(v => v.available === false).map(vehicle => (
              <div key={vehicle.id} style={{ 
                padding: '0.75rem', 
                margin: '0.5rem 0', 
                border: '1px solid #e2e8f0', 
                borderRadius: '8px', 
                backgroundColor: '#f8fafc' 
              }}>
                <h5 style={{ margin: 0, color: '#64748b' }}>{vehicle.typeName || vehicle.type}</h5>
                <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.875rem', color: '#64748b' }}>
                  {vehicle.message || vehicle.status}
                </p>
              </div>
            ))}
          </div>
        )}

        {/* Payment method selection */}
        <FormGroup>
          <Label>Choose Payment Method</Label>
          <div className="payment-options" style={{ 
            background: 'white', 
            borderRadius: '0.75rem', 
            padding: '1.5rem', 
            border: '1px solid #e2e8f0',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
            marginBottom: '2rem'
          }}>
            {paymentOptions.map(option => (
              <label key={option.value} style={{ 
                margin: '0 0 15px 0', 
                display: 'flex', 
                alignItems: 'center',
                padding: '12px',
                borderRadius: '8px',
                border: paymentMethod === option.value ? '2px solid #1e40af' : '1px solid #e2e8f0',
                backgroundColor: paymentMethod === option.value ? '#f0f9ff' : '#ffffff',
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}>
                <input
                  type="radio"
                  name="paymentMethod"
                  value={option.value}
                  checked={paymentMethod === option.value}
                  onChange={() => setPaymentMethod(option.value)}
                  style={{ cursor: 'pointer' }}
                />
                <option.icon size={20} style={{ color: '#1e40af', marginLeft: '12px' }} />
                <div style={{ marginLeft: '15px' }}>
                  <h4 style={{ margin: 0, color: '#1e293b', fontSize: '1rem' }}>{option.label}</h4>
                  <p style={{ margin: '4px 0 0', fontSize: '0.875rem', color: '#64748b' }}>{option.description}</p>
                </div>
              </label>
            ))}
          </div>
        </FormGroup>

        <ButtonGroup>
          <Button variant="secondary" onClick={onPrevious}>
            Previous
          </Button>
          <Button
            variant="success"
            onClick={handleSubmitWithPayment}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            disabled={!selectedVehicleTypeUUId}
          >
            Submit Request
          </Button>
        </ButtonGroup>
          </Body>
        </>
      )}
    </motion.div>
  );
};
